// @flow

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import {
  type RunStateMachine,
  type StateMachineRegistry,
  createStateMachineRegistry,
} from 'app/common/StateMachines';

import { type StateMachine, type StateValue } from 'xstate';

const StateMachinesContext = createContext<StateMachineRegistry>(
  createStateMachineRegistry()
);

const { Provider } = StateMachinesContext;

export { Provider as StateMachinesProvider };

// This function licensed (MIT) from: https://github.com/carlbarrdahl/redux-xstate/
const getActions = states =>
  Object.keys(states)
    .map(key => {
      const state = states[key];
      const actions = Object.keys(state.on || {});

      return state.states ? getActions(state.states).concat(actions) : actions;
    })
    .reduce((a, b) => a.concat(b), [])
    // De-duplication
    .filter((key, pos, arr) => arr.indexOf(key) === pos);

type UseStateMachineHook = () => StateValue;

export const useStateMachine = (machine: StateMachine): UseStateMachineHook => {
  const validActions = useMemo(() => getActions(machine.config.states), [
    machine,
  ]);
  const registry = useContext(StateMachinesContext);

  const machineStateRef = useRef(machine.initialState);

  const onTransitionMachine: RunStateMachine = useCallback(
    (getState, action) => {
      if (validActions.includes(action.type)) {
        machineStateRef.current = machine.transition(
          machineStateRef.current,
          action,
          getState()
        );
      }
    },
    [machine, validActions]
  );

  const getCurrentMachineState = useCallback(
    () => machineStateRef.current.value,
    [machineStateRef]
  );

  useEffect(() => {
    registry.register(onTransitionMachine);
    return () => {
      registry.unregister(onTransitionMachine);
    };
  }, [onTransitionMachine, registry]);

  return getCurrentMachineState;
};
